import { apiClient } from '@/services/api-client';
import type {
  CustomerPasswordChangeInput,
  CustomerPasswordChangeResponse,
  CustomerPasswordSetInput,
} from '@/types/customer-password';

export type CustomerPasswordChangeErrorItem = {
  id?: string;
  status?: string;
  code?: string;
  title?: string;
  detail?: string;
  source?: {
    pointer?: string;
  };
};

export class CustomerPasswordChangeRequestError extends Error {
  status?: number;
  errors?: CustomerPasswordChangeErrorItem[];
  payload?: unknown;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
    this.errors = readErrors(payload);
  }
}

function readErrors(payload: unknown): CustomerPasswordChangeErrorItem[] {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const record = payload as { errors?: unknown };
  if (!Array.isArray(record.errors)) {
    return [];
  }

  return record.errors.filter((entry): entry is CustomerPasswordChangeErrorItem => Boolean(entry && typeof entry === 'object'));
}

function resolveErrorMessage(errors: CustomerPasswordChangeErrorItem[], fallback: string) {
  if (!errors.length) {
    return fallback;
  }

  return (
    errors.find((error) => error.detail)?.detail ??
    errors.find((error) => error.title)?.title ??
    fallback
  );
}

class CustomerPasswordService {
  private async requestPassword(
    payload: Record<string, unknown>,
    fallbackMessage: string,
    errorMessage: string
  ): Promise<CustomerPasswordChangeResponse> {
    try {
      const response = await apiClient.patch<{ meta?: { message?: string } }>(
        '/api/v1/auth/customer/password',
        payload,
        {
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
          },
        },
      );

      const message = response.data.meta?.message ?? fallbackMessage;
      return { message };
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: unknown } })?.response;
      if (response) {
        const errors = readErrors(response.data);
        const message = resolveErrorMessage(errors, errorMessage);
        throw new CustomerPasswordChangeRequestError(message, response.status, response.data);
      }

      throw new CustomerPasswordChangeRequestError(errorMessage, undefined, error);
    }
  }

  async updatePassword(input: CustomerPasswordChangeInput): Promise<CustomerPasswordChangeResponse> {
    return this.requestPassword(
      {
        data: {
          type: 'customer-password-changes',
          attributes: {
            current_password: input.currentPassword,
            password: input.password,
            password_confirmation: input.passwordConfirmation,
          },
        },
      },
      'Password updated successfully.',
      'Unable to update password.',
    );
  }

  async setPassword(input: CustomerPasswordSetInput): Promise<CustomerPasswordChangeResponse> {
    try {
      const response = await apiClient.post<{ meta?: { message?: string } }>(
        '/api/v1/auth/customer/password',
        {
          data: {
            type: 'customer-password-sets',
            attributes: {
              password: input.password,
              password_confirmation: input.passwordConfirmation,
            },
          },
        },
        {
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
          },
        },
      );

      const message = response.data.meta?.message ?? 'Password set successfully.';
      return { message };
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: unknown } })?.response;
      if (response) {
        const errors = readErrors(response.data);
        const message = resolveErrorMessage(errors, 'Unable to set password.');
        throw new CustomerPasswordChangeRequestError(message, response.status, response.data);
      }

      throw new CustomerPasswordChangeRequestError('Unable to set password.', undefined, error);
    }
  }
}

export const customerPasswordService = new CustomerPasswordService();
